'use client';

/**
 * CartGiftsSelectorWidget
 *
 * Bottom sheet component for selecting cart-level gifts.
 * Matches mobile implementation pattern using withAppBottomSheet HOC + forwardRef + useImperativeHandle
 *
 * Key features:
 * - Uses ref pattern to expose openBottomSheet() and getSelectedGifts() methods
 * - Renders null, content is injected into bottom sheet via bottomSheet.inject()
 * - Uses useCartGiftSelection hook for cart-level gift selection state
 *
 * Uses real withAppBottomSheet HOC from mario-nextjs-react-ui-core
 * (responsive: Modal on desktop, BottomSheet on mobile viewport)
 */

import {
    withAppBottomSheet,
    type AppBottomSheetHeaderProps,
    type AppBottomSheetExtraParamsProps,
    type AppBottomSheetBackdropProps,
    type AppBottomSheetFooterProps,
    withDialog,
} from '@kidslabs/mario-nextjs-react-ui-core';

import React, {
    useCallback,
    useImperativeHandle,
    useMemo,
    forwardRef,
    type ForwardRefRenderFunction,
    type ForwardedRef,
} from 'react';
import { useObservable } from '@legendapp/state/react';
import type { CartGiftsSelectorWidgetProps, CartGiftsSelectorWidgetRef } from './cart-gifts-selector-widget.types';
import type { SelectedGift } from '../../types/gift-selection.types';
import { CartGiftsSelectorWidgetContents } from './cart-gifts-selector-widget-contents';
import { GiftSelectorBottomSheetFooter } from '../gift-selector-bottom-sheet-footer/gift-selector-bottom-sheet-footer';
import { useCartGiftSelection } from '../../hooks';
import styles from './cart-gifts-selector-widget.module.css';
import { useTranslation } from 'next-i18next';

const CartGiftsSelectorWidgetInner: ForwardRefRenderFunction<
    CartGiftsSelectorWidgetRef,
    CartGiftsSelectorWidgetProps
> = (props: CartGiftsSelectorWidgetProps, ref: ForwardedRef<CartGiftsSelectorWidgetRef>) => {
    const {
        bottomSheet,
        cartLevelGiftRules,
        onClose,
        onConfirm: onConfirmInput,
        showSelectedOnly = false,
        renderJnjectedContent,
        hideDialog,
        setDialogOptions,
        showDialog,
    } = props;

    const { t } = useTranslation('flash-sale');
    /**
     * Observable boolean tracks whether a confirm mutation is in flight.
     * Using Legend-State observable instead of useState because the footer
     * is statically injected via bottomSheet.inject() — a regular state
     * change would NOT propagate to the already-injected React tree.
     * The footer component is an observer() that subscribes to this
     * observable and re-renders reactively when setting confirming state.
     */
    const isConfirming$ = useObservable(false);
    const draftSelectedGifts$ = useObservable([] as SelectedGift[]);
    const visibleRuleIds$ = useObservable([] as number[]);

    // Use cart-specific gift selection hook to access store
    const cartGiftSelection = useCartGiftSelection({
        createNewSession: false,
    });

    const onBottomSheetClose = useCallback(() => {
        // Prevent closing while mutation is running
        if (isConfirming$.peek()) return;
        bottomSheet?.close();
        onClose?.();
    }, [bottomSheet, onClose, isConfirming$]);

    const handleErrorBottomSheet = useCallback(
        (message: string, onConfirm?: () => void, onDismiss?: () => void) => {
            setDialogOptions({
                title: t('text_notification', 'Thông báo'),
                content: message,
                acceptLabel: t('text_agree', 'Đồng ý'),
                dismissLabel: t('text_cancel', 'Hủy'),
                loading: false,
                onAccept: onConfirm
                    ? () => {
                          onConfirm?.();
                          hideDialog();
                      }
                    : undefined,
                onDismiss: onDismiss
                    ? () => {
                          onDismiss?.();
                          hideDialog();
                      }
                    : undefined,
            });
            showDialog();
        },
        [setDialogOptions, showDialog, hideDialog, t]
    );

    const onConfirm = useCallback(() => {
        const selectedGifts = draftSelectedGifts$.get() ?? [];
        const visibleRuleIds = new Set(visibleRuleIds$.get() ?? []);

        if (!onConfirmInput) {
            const currentGifts = cartGiftSelection.store$.selectedGifts.get() ?? [];
            const giftsFromOtherRules = currentGifts.filter(g => !visibleRuleIds.has(g.ruleId ?? 0));
            cartGiftSelection.store$.selectedGifts.set([...giftsFromOtherRules, ...selectedGifts]);
            bottomSheet?.close();
            return;
        }

        // Enter loading state
        isConfirming$.set(true);

        // Delegate mutation to parent via callback pattern
        onConfirmInput({
            data: selectedGifts,
            onSuccess: () => {
                const currentGifts = cartGiftSelection.store$.selectedGifts.get() ?? [];
                const giftsFromOtherRules = currentGifts.filter(g => !visibleRuleIds.has(g.ruleId ?? 0));
                cartGiftSelection.store$.selectedGifts.set([...giftsFromOtherRules, ...selectedGifts]);
                isConfirming$.set(false);
                bottomSheet?.close();
                onClose?.();
            },
            onError: (errorMessage, onConfirm, onDismiss) => {
                isConfirming$.set(false);
                console.error('[CartGiftsSelectorWidget] Confirm failed:', errorMessage);
                handleErrorBottomSheet(errorMessage, onConfirm, onDismiss);
                // Bottom sheet stays open so user can retry
            },
        });
    }, [
        bottomSheet,
        onConfirmInput,
        onClose,
        cartGiftSelection.store$,
        isConfirming$,
        draftSelectedGifts$,
        visibleRuleIds$,
        handleErrorBottomSheet,
    ]);

    const headerProps = useMemo<AppBottomSheetHeaderProps>(
        () => ({
            title: showSelectedOnly ? '' : t('text_gift_choose_accept-change', 'Chọn quà tặng'),
            titleProps: { className: styles.titleBottomSheet },
        }),
        [t, showSelectedOnly]
    );

    const extraParams: AppBottomSheetExtraParamsProps = useMemo(() => {
        return {
            enableDynamicSizing: false,
            scrollEnabled: false,
            enableContentPanningGesture: false,
            snapPoints: ['85%', '85%'],
        };
    }, []);

    const footerProps = useMemo<AppBottomSheetFooterProps>(
        () => ({
            component: showSelectedOnly ? null : (
                <GiftSelectorBottomSheetFooter
                    onBottomSheetClose={onBottomSheetClose}
                    onConfirm={onConfirm}
                    isConfirming$={isConfirming$}
                />
            ),
        }),
        [onBottomSheetClose, onConfirm, isConfirming$, showSelectedOnly]
    );

    const backdropProps: AppBottomSheetBackdropProps = useMemo(() => {
        return {
            pressBehavior: 'close',
        };
    }, []);

    const renderInjectedBottomSheet = useCallback(() => {
        if (!renderJnjectedContent || !showSelectedOnly) {
            return null;
        }

        return renderJnjectedContent();
    }, [showSelectedOnly, renderJnjectedContent]);

    // Open bottom sheet with optional rule filter (for "Đổi quà" flow)
    const openBottomSheet = useCallback(
        (filterRuleId?: number) => {
            const filteredRules = filterRuleId
                ? cartLevelGiftRules.filter(rule => rule.rule_id === filterRuleId)
                : cartLevelGiftRules;
            visibleRuleIds$.set(filteredRules.map(rule => rule.rule_id));

            const view = renderInjectedBottomSheet() ?? (
                <CartGiftsSelectorWidgetContents
                    cartLevelGiftRules={filteredRules}
                    draftSelectedGifts$={draftSelectedGifts$}
                />
            );

            bottomSheet?.inject(view, headerProps, extraParams, footerProps, backdropProps);
            bottomSheet?.show();
        },
        [
            cartLevelGiftRules,
            footerProps,
            bottomSheet,
            headerProps,
            extraParams,
            backdropProps,
            renderInjectedBottomSheet,
            draftSelectedGifts$,
            visibleRuleIds$,
        ]
    );

    // Hàm để lấy selected gifts từ store
    const getSelectedGifts = useCallback(() => {
        return draftSelectedGifts$.get();
    }, [draftSelectedGifts$]);

    // Expose hàm cho parent qua ref
    useImperativeHandle(
        ref,
        () => ({
            openBottomSheet,
            getSelectedGifts,
        }),
        [openBottomSheet, getSelectedGifts]
    );

    return null;
};

CartGiftsSelectorWidgetInner.displayName = 'CartGiftsSelectorWidgetInner';

// Export component với forwardRef và HOC
export const CartGiftsSelectorWidget = withAppBottomSheet(withDialog(forwardRef(CartGiftsSelectorWidgetInner)));

export default CartGiftsSelectorWidget;
